import * as React from 'react';
import TextWithReferences from '../PageElements/TextWithReferences/TextWithReferences';
import * as _ from 'underscore';
import { GameHighlightTermsData, GameHighlightTermsState } from '../Games.types';
import { useLocation } from 'react-router-dom';
import { getUrlWithTransientStateProperties } from '../Sessions/Session.logic';
import { useAutoSaveOnPageLeave } from '../../../store/hooks';
import { getGameState, saveGameState } from '../../../services/DBService/DbService';
import { GameStateDB } from '../../../data/datatypes';

type Properties = {
    data: GameHighlightTermsData;
    setNextButtonLabel: (label: string | undefined) => void;
};

const HighlightTerms = (props: Properties) => {
    const { showSolutions, items, caption, desc } = props.data;

    const [step, setStep] = React.useState<'playing' | 'result'>('playing');
    const [termsActive, setTermsActive] = React.useState<string[]>([]);

    const path = useLocation().pathname;
    const dbId: `game_${string}` = `game_${getUrlWithTransientStateProperties(path)}`;

    useAutoSaveOnPageLeave(
        [step, termsActive],
        async () => {
            const gameState: GameStateDB = {
                _id: dbId,
                state: {
                    step,
                    termsActive
                }
            };
            await saveGameState(gameState);
        },
        [path],
        async () => {
            const state = await getGameState(dbId);
            if (state) {
                setStep((state.state as GameHighlightTermsState).step);
                setTermsActive((state.state as GameHighlightTermsState).termsActive);
            }
        }
    );

    // set next-Button label
    React.useEffect(() => {
        props.setNextButtonLabel('Spiel beenden');
        return () => {
            props.setNextButtonLabel(undefined);
        };
    }, [props.setNextButtonLabel]);

    const reset = () => {
        setStep('playing');
        setTermsActive([]);
    };

    const menuButtonClicked = () => {
        if (step === 'playing' && showSolutions) {
            setStep('result');
        } else if (step === 'result') {
            reset();
        }
    };

    const toggleActive = (itemText: string) => {
        const newTermsActive = [...termsActive];

        let i = newTermsActive.indexOf(itemText);
        if (i === -1) {
            // add itemIdx to active terms
            newTermsActive.push(itemText);
        } else {
            // remove itemIdx from active terms
            newTermsActive.splice(i, 1);
        }
        setTermsActive(newTermsActive);
    };

    let menuButtonLabel = 'Ergebnis anzeigen';
    if (step === 'result') {
        menuButtonLabel = 'Neu starten';
    }

    return (
        <div className="game">
            <div className="game-content">
                <div className="highlight-terms">
                    <h1>{caption}</h1>
                    <p><TextWithReferences text={desc || ''} /></p>
                    <p className="highlight-terms-text">
                        {items.map((item, i) => {
                            switch (item.type) {
                                case 'text':
                                    return <span key={i}>{item.text}</span>;

                                case 'term':
                                    let onClick: () => any = () => toggleActive(item.text);
                                    let classNameActive = 'term-active';
                                    let classNameInactive = 'term-inactive';

                                    if (showSolutions && step === 'result') {
                                        // display results only
                                        onClick = () => undefined;
                                        classNameActive = 'term-active-result';
                                        classNameInactive = 'term-inactive-result';
                                    }

                                    const isActive = termsActive.indexOf(item.text) !== -1;
                                    return (
                                        <span
                                            className={isActive === true ? classNameActive : classNameInactive}
                                            onClick={onClick}
                                            key={i}
                                        >
                                            {item.text}
                                        </span>
                                    );

                                default:
                                    throw 'Unerwarteter Item-Typ in HighlightTermsGame';
                            }
                        })}
                    </p>
                </div>
            </div>
            {showSolutions && <div className="game-menu">
                <button onClick={menuButtonClicked} className="primary">{menuButtonLabel}</button>
            </div>}
        </div>
    );
};

export default HighlightTerms;